import { supabase } from '../lib/supabase';
import { ApiKey, ApiUsageStats } from '../types';

export class ApiKeyService {
  // Create a new API key
  static async createApiKey(
    userId: string,
    roomId: string,
    keyData: {
      name: string;
      permissions: {
        read: boolean;
        write: boolean;
      };
      expiresAt?: Date;
      rateLimit?: {
        requestsPerMinute: number;
        requestsPerHour: number;
      };
      webhookUrl?: string;
    }
  ): Promise<ApiKey> {
    // Generate a secure API key
    const apiKeyValue = `rsk_${crypto.randomUUID().replace(/-/g, '')}`;
    
    // Hash the API key for storage (in production, use proper hashing)
    const keyHash = await this.hashApiKey(apiKeyValue);
    
    const insertData: any = {
      user_id: userId,
      room_id: roomId,
      name: keyData.name,
      key_hash: keyHash,
      permissions: keyData.permissions,
      active: true
    };

    if (keyData.expiresAt) {
      insertData.expires_at = keyData.expiresAt.toISOString();
    }

    if (keyData.rateLimit) {
      insertData.rate_limit = keyData.rateLimit;
    }

    if (keyData.webhookUrl) {
      insertData.webhook_url = keyData.webhookUrl;
    }

    const { data, error } = await supabase
      .from('api_keys')
      .insert(insertData)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      key: apiKeyValue, // Return the unhashed key only once
      userId: data.user_id,
      roomId: data.room_id,
      keyHash: data.key_hash,
      permissions: data.permissions,
      createdAt: new Date(data.created_at).getTime(),
      updatedAt: new Date(data.updated_at).getTime(),
      active: data.active,
      ...(data.expires_at && { expiresAt: new Date(data.expires_at).getTime() }),
      ...(data.rate_limit && { rateLimit: data.rate_limit }),
      ...(data.webhook_url && { webhookUrl: data.webhook_url }),
      ...(data.last_used_at && { lastUsedAt: new Date(data.last_used_at).getTime() })
    };
  }

  // Get all API keys for a user
  static async getUserApiKeys(userId: string): Promise<ApiKey[]> {
    const { data, error } = await supabase
      .from('api_keys')
      .select(`
        *,
        rooms!inner (
          id,
          name
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(key => ({
      id: key.id,
      name: key.name,
      key: '***hidden***', // Never return the actual key after creation
      userId: key.user_id,
      roomId: key.room_id,
      keyHash: key.key_hash,
      permissions: key.permissions,
      createdAt: new Date(key.created_at).getTime(),
      updatedAt: new Date(key.updated_at).getTime(),
      active: key.active,
      ...(key.expires_at && { expiresAt: new Date(key.expires_at).getTime() }),
      ...(key.rate_limit && { rateLimit: key.rate_limit }),
      ...(key.webhook_url && { webhookUrl: key.webhook_url }),
      ...(key.last_used_at && { lastUsedAt: new Date(key.last_used_at).getTime() })
    }));
  }

  // Get API keys for a specific room
  static async getRoomApiKeys(roomId: string, userId: string): Promise<ApiKey[]> {
    const { data, error } = await supabase
      .from('api_keys')
      .select('*')
      .eq('room_id', roomId)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return (data || []).map(key => ({
      id: key.id,
      name: key.name,
      key: '***hidden***',
      userId: key.user_id,
      roomId: key.room_id,
      keyHash: key.key_hash,
      permissions: key.permissions,
      createdAt: new Date(key.created_at).getTime(),
      updatedAt: new Date(key.updated_at).getTime(),
      active: key.active,
      ...(key.expires_at && { expiresAt: new Date(key.expires_at).getTime() }),
      ...(key.rate_limit && { rateLimit: key.rate_limit }),
      ...(key.webhook_url && { webhookUrl: key.webhook_url }),
      ...(key.last_used_at && { lastUsedAt: new Date(key.last_used_at).getTime() })
    }));
  }

  // Update API key
  static async updateApiKey(
    keyId: string,
    updates: {
      name?: string;
      active?: boolean;
      expiresAt?: Date | null;
      rateLimit?: {
        requestsPerMinute: number;
        requestsPerHour: number;
      } | null;
      webhookUrl?: string | null;
    }
  ): Promise<void> {
    const updateData: any = {};

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.active !== undefined) updateData.active = updates.active;
    if (updates.expiresAt !== undefined) {
      updateData.expires_at = updates.expiresAt ? updates.expiresAt.toISOString() : null;
    }
    if (updates.rateLimit !== undefined) updateData.rate_limit = updates.rateLimit;
    if (updates.webhookUrl !== undefined) updateData.webhook_url = updates.webhookUrl;

    const { error } = await supabase
      .from('api_keys')
      .update(updateData)
      .eq('id', keyId);

    if (error) throw error;
  }

  // Delete API key
  static async deleteApiKey(keyId: string): Promise<void> {
    const { error } = await supabase
      .from('api_keys')
      .delete()
      .eq('id', keyId);

    if (error) throw error;
  }

  // Get API usage statistics
  static async getApiUsageStats(keyId: string): Promise<ApiUsageStats> {
    const { data, error } = await supabase
      .rpc('get_api_usage_stats', { key_id: keyId });

    if (error) throw error;

    const stats = data[0] || {
      total_requests: 0,
      requests_today: 0,
      requests_this_hour: 0,
      avg_response_time: 0,
      error_rate: 0
    };

    return {
      totalRequests: parseInt(stats.total_requests),
      requestsToday: parseInt(stats.requests_today),
      errorRate: parseFloat(stats.error_rate)
    };
  }

  // Validate API key (for API endpoints)
  static async validateApiKey(apiKey: string): Promise<{
    valid: boolean;
    keyId?: string;
    userId?: string;
    roomId?: string;
    permissions?: {
      read: boolean;
      write: boolean;
    };
  }> {
    try {
      const { data, error } = await supabase
        .rpc('validate_api_key', { api_key_value: apiKey });

      if (error || !data || data.length === 0) {
        return { valid: false };
      }

      const keyData = data[0];

      if (!keyData.active || keyData.expired) {
        return { valid: false };
      }

      return {
        valid: true,
        keyId: keyData.key_id,
        userId: keyData.user_id,
        roomId: keyData.room_id,
        permissions: keyData.permissions
      };
    } catch (error) {
      console.error('API key validation error:', error);
      return { valid: false };
    }
  }

  // Log API usage (for API endpoints)
  static async logApiUsage(
    keyId: string,
    endpoint: string,
    method: string,
    statusCode: number,
    responseTimeMs?: number
  ): Promise<void> {
    try {
      await supabase.rpc('log_api_usage', {
        key_id: keyId,
        endpoint_path: endpoint,
        http_method: method,
        status_code: statusCode,
        response_time_ms: responseTimeMs
      });
    } catch (error) {
      console.error('Failed to log API usage:', error);
    }
  }

  // Check rate limits (for API endpoints)
  static async checkRateLimit(keyId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('check_rate_limit', { key_id: keyId });

      if (error) {
        console.error('Rate limit check error:', error);
        return false;
      }

      return data;
    } catch (error) {
      console.error('Rate limit check error:', error);
      return false;
    }
  }

  // Helper function to hash API keys
  private static async hashApiKey(apiKey: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(apiKey);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }
}